(function () {
  /**
   * ============================================================
   *  Evitavonni — Right of Withdrawal form: styling
   * ============================================================
   *  Standalone script, separate from the trade account form script.
   *  Gives the Shopify Forms embed on /pages/right-of-withdrawal the
   *  same field look as the trade form: brand font, uppercase labels,
   *  underline fields, ink navy button, mobile behavior.
   *
   *  Loaded only by sections/ev-withdrawal-form.liquid, so it only
   *  runs on the withdrawal template. On top of that it only touches
   *  a form inside <main>, so a popup form elsewhere on the page
   *  (e.g. newsletter) is left alone.
   *
   *  Check the browser console for "[ev-withdrawal-form]" messages
   *  if something looks off.
   * ============================================================
   */

  if (window.__evWithdrawalFormLoaded) return;
  window.__evWithdrawalFormLoaded = true;

  /**
   * 1. Font, colors, labels, button.
   *    The embed reads its typography from CSS custom properties on
   *    :host, so overriding those in our own <style> (added after the
   *    embed's stylesheet) restyles all internal components at once.
   */
  const FONT_CSS = `
    :host {
      --forms-body-font: normal 400 14px "futura-pt", sans-serif !important;
      --forms-heading-font: normal 700 24px "futura-pt", sans-serif !important;
      --button-text-color: #ffffff !important;
      --button-border-radius: 0px !important;

      --ev-vellum-white: #F1EFEB;
      --ev-ink-navy: #120E27;
      --ev-soft-mist: #D7D2CB;
      --ev-dark-mist: #8B867E;
      --ev-taupe: #B7ACA2;
      --ev-charcoal: #2B2C2D;
      --ev-stone-grey: #B4B4B4;
    }

    label,
    legend {
      font-family: "futura-pt", sans-serif !important;
      letter-spacing: 0.2em !important;
      text-transform: uppercase !important;
      font-size: 11px !important;
      font-weight: 600 !important;
      line-height: 1.5em !important;
      text-decoration: none !important;
      color: var(--ev-dark-mist) !important;
    }

    [data-testid="btn-form-submit"] {
      font-family: "futura-pt", sans-serif !important;
      color: #ffffff !important;
      background-color: var(--ev-ink-navy) !important;
      text-transform: uppercase !important;
      font-weight: 600 !important;
      border-radius: 0 !important;
      text-align: center !important;
      letter-spacing: 0.2em !important;
      padding: 16px 32px !important;
      font-size: 11px !important;
      line-height: 1em !important;
    }
  `;

  /**
   * 2. Layout: single column, generous spacing, underline fields.
   */
  const LAYOUT_CSS = `
    :host {
      --container-max-w: 700px !important;
      --inline-container-max-width: 700px !important;
    }

    form[data-testid="form"] {
      display: flex !important;
      flex-direction: column !important;
      gap: 32px !important;
    }

    section[data-sizing="form-wrapper"] {
      min-height: auto !important;
    }

    /* Replace the bordered boxes with a single line under each field. */
    form[data-testid="form"] [class*="formFieldContainer" i] {
      border: none !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    form[data-testid="form"] input:not([type="checkbox"]):not([type="radio"]),
    form[data-testid="form"] textarea,
    form[data-testid="form"] select,
    form[data-testid="form"] [role="combobox"],
    form[data-testid="form"] button[aria-haspopup="listbox"] {
      border: none !important;
      border-bottom: 1px solid var(--ev-stone-grey) !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    form[data-testid="form"] input:not([type="checkbox"]):not([type="radio"]):focus,
    form[data-testid="form"] textarea:focus,
    form[data-testid="form"] select:focus,
    form[data-testid="form"] [role="combobox"]:focus,
    form[data-testid="form"] button[aria-haspopup="listbox"]:focus {
      outline: none !important;
      border-bottom-color: var(--ev-ink-navy) !important;
    }

    /* Keep the floating label clear of the entered value. */
    form[data-testid="form"] [class*="formFieldContainer" i] [class*="inputFilled" i] {
      transform: translateY(-22px) scale(0.85) !important;
    }
  `;

  /**
   * 3. Mobile + iOS zoom fix (Safari zooms in on inputs < 16px).
   */
  const MOBILE_CSS = `
    form[data-testid="form"] input,
    form[data-testid="form"] textarea,
    form[data-testid="form"] select {
      font-size: 16px !important;
    }

    :host {
      box-sizing: border-box;
    }

    :host *,
    :host *::before,
    :host *::after {
      box-sizing: inherit;
    }

    @media (max-width: 640px) {
      :host {
        --container-max-w: 100% !important;
        --inline-container-max-width: 100% !important;
      }

      form[data-testid="form"] {
        gap: 28px !important;
        padding: 0 20px !important;
      }

      label,
      legend {
        font-size: 10px !important;
        letter-spacing: 0.12em !important;
      }

      [data-testid="btn-form-submit"] {
        width: 100% !important;
        padding: 16px 24px !important;
      }
    }
  `;

  function injectStyles(shadow) {
    if (shadow.querySelector('style[data-ev-withdrawal-styles]')) return;

    const style = document.createElement('style');
    style.setAttribute('data-ev-withdrawal-styles', 'true');
    style.textContent = FONT_CSS + LAYOUT_CSS + MOBILE_CSS;
    shadow.appendChild(style);
  }

  // Only the form in the page's main content, never a popup elsewhere.
  function getShadow() {
    const hosts = document.querySelectorAll('main shopify-forms-embed');

    for (const host of hosts) {
      const shadow = host.shadowRoot;
      if (shadow && shadow.querySelector('form[data-testid="form"]')) {
        return shadow;
      }
    }

    return null;
  }

  function init() {
    try {
      const shadow = getShadow();
      if (!shadow) return false;

      injectStyles(shadow);
      return true;
    } catch (err) {
      console.error('[ev-withdrawal-form] init failed:', err);
      return false;
    }
  }

  // The embed's shadow root often fills in after load (hydration), and
  // mutations inside a shadow root don't bubble to the document, so we
  // watch both the page and each host's shadow root, for up to 20s.
  if (init()) return;

  const MAX_WAIT_MS = 20000;
  const observedHosts = new WeakSet();
  let settled = false;

  function tryInit() {
    if (settled) return true;
    if (init()) {
      settled = true;
      return true;
    }
    return false;
  }

  function watchHosts() {
    document.querySelectorAll('main shopify-forms-embed').forEach((host) => {
      if (observedHosts.has(host) || !host.shadowRoot) return;

      observedHosts.add(host);
      const shadowObserver = new MutationObserver(() => {
        if (tryInit()) shadowObserver.disconnect();
      });
      shadowObserver.observe(host.shadowRoot, { childList: true, subtree: true });
      setTimeout(() => shadowObserver.disconnect(), MAX_WAIT_MS);
    });
  }

  const rootObserver = new MutationObserver(() => {
    if (tryInit()) {
      rootObserver.disconnect();
      return;
    }
    watchHosts();
  });

  rootObserver.observe(document.documentElement, { childList: true, subtree: true });
  watchHosts();
  setTimeout(() => rootObserver.disconnect(), MAX_WAIT_MS);
})();